/**
 * Document Title Hook
 *
 * Updates the browser document title based on current language and branding config.
 * Resolves the brand name via the single `useBrandName()` source of truth
 * (backend config first, i18n fallback) rather than duplicating the logic.
 * Should be used once at the app root level.
 */

import { useEffect } from 'react';
import { useBrandName } from './useBrandName';

/**
 * Updates document.title with the branded app name.
 * The name comes from `useBrandName()`, which reads backend config and falls
 * back to the i18n translation while config is loading.
 * Call this once in your App component.
 *
 * @example
 * function App() {
 *   useDocumentTitle();
 *   return <Router>...</Router>;
 * }
 */
export function useDocumentTitle(): void {
  const brandName = useBrandName();

  useEffect(() => {
    document.title = brandName;
  }, [brandName]);
}
